import { capitalize, find, findIndex, toLower } from 'lodash';
import { schema } from 'normalizr';
import { SWTYPES } from '../settings';
import { formatDate, isNone, noneToNull, replaceBrackets } from '../utils';

// We use this Normalizr schemas to transform API responses from a nested form
// to a flat form where repos and users are placed in `entities`, and nested
// JSON objects are replaced with their IDs. This is very convenient for
// consumption by reducers, because we can easily build a normalized tree
// and keep it updated as we fetch more data.

// Read more about Normalizr: https://github.com/paularmstrong/normalizr

// GitHub's API may return results with uppercase letters while the query
// doesn't contain any. For example, "someuser" could result in "SomeUser"
// leading to a frozen UI as it wouldn't find "someuser" in the entities.
// That's why we're forcing lower cases down there.

const aliasListSchema = new schema.Entity('orgs', {}, {
    idAttribute: 'orgAlias',
    processStrategy: (value, parent, key) => ({
        key: value.orgAlias,
        title: `[${value.orgAlias}] ${value.orgName}`
    })
});

const componentsSchema = new schema.Entity('components', {}, {
    idAttribute: 'name',
    processStrategy: (value, parent, key) => ({
        key: value.name,
        title: value.name
    })
});

const orgsSchema = new schema.Entity('organisations', {}, {
    idAttribute: 'orgAlias',
    processStrategy: (value, parent, key) => ({
        key: value.orgAlias,
        alias: value.orgAlias,
        name: value.orgName,
        phone: noneToNull(value.phone),
        email: noneToNull(value.email)
    })
});

const orgSchema = new schema.Entity('organisations', {}, {
    idAttribute: 'orgAlias',
    processStrategy: (value, parent, key) => ({
        key: value.orgAlias,
        alias: value.orgAlias,
        name: value.orgName,
        addr: noneToNull(value.address),
        phone: noneToNull(value.phone),
        email: noneToNull(value.email),
        notes: noneToNull(value.notes),
        contracts: value.contracts || []
    })
});

const orgUsersSchema = new schema.Entity('users', {}, {
    idAttribute: "orgAlias",
    processStrategy: (value, parent, key) => ({
        name: value.orgName,
        users: value.users.map(user => ({
            key: user.email,
            email: user.email,
            name: user.fullName,
            grant: user.grantName,
            disabled: user.disabled == "1" || user.expiryDate < formatDate(Date.now(), "YYYY-MM-DD"),
            expired: user.expiryDate,
            org: user.defaultOrgName
        }))
    })
});

const orgIssuesSchema = new schema.Entity('issues', {}, {
    idAttribute: "orgAlias",
    processStrategy: (value, parent, key) => ({
        name: value.orgName,
        issues: value.issues.map(issue => ({
            key: issue.ticketRef,
            ref: issue.ticketRef,
            summary: issue.summary,
            reporter: replaceBrackets(issue.submittedBy),
            date: issue.submitDatetime,
            status: issue.status
        }))
    })
});

const usersSchema = new schema.Entity('users', {}, {
    idAttribute: "email",
    processStrategy: (value, parent, key) => ({
        key: value.email,
        email: value.email,
        name: value.fullName,
        grant: value.grantName,
        alias: value.defaultOrgAlias,
        org: value.defaultOrgName,
        disabled: value.disabled == "1" || value.expiryDate < formatDate(Date.now(), "YYYY-MM-DD"),
        expired: value.expiryDate
    })
});

const userSchema = new schema.Entity('users', {}, {
    idAttribute: (value, parent, key) => toLower(value.email),
    processStrategy: (value, parent, key) => ({
        key: value.email,
        email: value.email,
        name: value.fullName,
        orgName: value.defaultOrgName,
        orgAlias: value.defaultOrgAlias,
        grant: value.grantName,
        disabled: value.disabled == "1",
        grants: value.grants
            ? value.grants
            : [{
                grantName: value.grantName,
                orgName: value.defaultOrgName,
                orgAlias: value.defaultOrgAlias,
                issuedBy: value.issuedBy,
                expiryDate: value.expiryDate
            }]
    })
});

const currentUserSchema = new schema.Entity('users', {}, {
    idAttribute:  (value, parent, key) => toLower(value.email),
    processStrategy: (value, parent, key) => ({
        email: value.email,
        fullName: value.fullName,
        adminRead: value.adminRead == 1,
        adminWrite: value.adminWrite == 1,
        auditRead: value.auditRead == 1,
        contractsRead: value.contractsRead == 1,
        contractsWrite: value.contractsWrite == 1,
        jiraRead: value.jiraRead == 1,
        jiraWrite: value.jiraWrite == 1,
        login: value.login == 1,
        orgRead: value.orgRead == 1,
        orgWrite: value.orgWrite == 1,
        packageRead: value.packageRead == 1,
        packageWrite: value.packageWrite == 1,
        shcRead: value.shcRead == 1,
        shcWrite: value.shcWrite == 1,
        t4Read: value.t4Read == 1,
        t4Write: value.t4Write == 1,
        userWrite: value.userWrite == 1,
        userDelete: value.userDelete == 1
    })
});

const issuesSchema = new schema.Entity('issues', {}, {
    idAttribute: "ticketRef",
    processStrategy: (value, parent, key) => ({
        key: value.ticketRef,
        ref: value.ticketRef,
        summary: value.summary,
        reporter: replaceBrackets(value.submittedBy),
        date: value.submitDatetime,
        status: value.status
    })
});

const issueSchema = new schema.Entity('issues', {}, {
    idAttribute: (value, parent, key) => toLower(value.ticketRef)
});

const issueCommentsSchema = new schema.Entity('comments', {}, {
    idAttribute: "commentRef",
    processStrategy: (value, parent, key) => ({
        key: value.commentRef,
        created: value.submitDatetime,
        author: value.submittedBy,
        body: value.commentBody,
        visibility: value.visibility
    })
});

const orgContractsSchema = new schema.Entity('contracts', {}, {
    idAttribute: (value, parent, key) => parent.indexOf(value), // "contractNumber",
    processStrategy: (value, parent, key) => ({
        key: parent.indexOf(value),
        name: value.contractName,
        endDate: value.endDate,
        org: value.orgName,
        alias: value.orgAlias,
        registeredBy: value.registeredBy,
        updatedBy: value.updatedBy
    })
});

const contractsSchema = new schema.Entity('contracts', {}, {
    idAttribute: 'contractName',
    processStrategy: (value, parent, key) => ({
        key: value.contractName,
        title: value.contractName,
        active: value.inactive == 0
    })
});

const grantsSchema = new schema.Entity('grants', {}, {
    idAttribute: 'grantName',
    processStrategy: (value, parent, key) => ({
        key: value.grantName,
        title: capitalize(value.grantName),
        visible: value.visible == "1",
        vsiOnly: value.vsiOnly == "1"
    })
});

const uploadContractsSchema = new schema.Entity("contracts", {}, {
    idAttribute: (value, parent, key) => parent.indexOf(value),
    processStrategy: (value, parent, key) => ({
        key: parent.indexOf(value),
        ...value
    })
});

const manageContractsSchema = new schema.Entity('contracts', {}, {
    idAttribute: (value, parent, key) => toLower(value.contractName),
    processStrategy: (value, parent, key) => ({
        key: value.contractNumber,
        name: value.contractName,
        active: value.inactive == 0,
        ...value
    })
});

const packagesSchema = new schema.Entity('packages', {}, {
    idAttribute: 'packageNumber',
    processStrategy: (value) => ({
            key: value.packageNumber,
            name: value.name,
            version: value.version,
            sysarch: value.sysarch,
            swtype: find(SWTYPES, { key: toLower(value.type) }) && find(SWTYPES, { key: toLower(value.type) }).hasOwnProperty("title")
                ? `${find(SWTYPES, { key: toLower(value.type) }).title} ${noneToNull(value.ecoRating) ? `(Rating-${value.ecoRating})` : ""}`
                : toLower(value.type),
            minos: value.minos,
            verified: value.verified,
            verifiedDate: value.verified == 1
                ? isNone(value.verifiedDatetime)
                    ? formatDate(new Date(Date.UTC(2021, 8, 30)), "YYYY-MM-DD HH:mm:SS")
                    : value.verifiedDatetime
                : "None",
            restart: value.restartRequired,
            ecoRating: noneToNull(value.ecoRating)
        })
});

const packageSchema = new schema.Entity('packages', {}, {
    idAttribute: (value, parent, key) => toLower(value[0]["packageNumber"]),
    processStrategy: (value, parent, key) => {
        const details = Array.isArray(value) ? value[0] : value;
        return ({
            key: details.packageNumber,
            name: details.name,
            version: details.version,
            sysarch: details.sysarch,
            swtype: toLower(details.type),
            minos: details.minos,
            description: details.description,
            restart: details.restartRequired,
            ecoRating: details.ecoRating,
            prereq: Array.isArray(details.prereq) ? details.prereq : [],
            requiredFor: Array.isArray(details.requiredFor) ? details.requiredFor : [],
            contracts: Array.isArray(details.contracts) ? details.contracts : [],
            registeredBy: details.registeredBy,
            registeredDatetime: details.registeredDatetime,
            style: toLower(details.style),
            verified: details.verified,
            verifiedBy: details.verifiedBy,
            verifiedDate: details.verifiedDatetime,
            files: Array.isArray(details.files)
                ? details.files.map(val => ({
                    key: val.packageFileNumber,
                    name: val.filename,
                    md5: val.md5,
                    size: val.downloadSize
                }))
                : []
        })
    }
});

const searchPackagesSchema = new schema.Entity('packages', {}, {
    idAttribute: "packageNumber",
    processStrategy: (value) => ({
        key: value.packageNumber,
        name: value.name,
        version: value.version,
        sysarch: value.sysarch,
        verified: value.verified
    })
});

const auditDetailsSchema = new schema.Entity('audit', {}, {
    idAttribute: "auditNumber"
});

// Schemas for Github API responses.
export const schemas = {
    ALIASES: [aliasListSchema],
    COMPONENTS: [componentsSchema],
    organisations: [orgsSchema],
    ORG: [orgSchema],
    ORG_USERS: [orgUsersSchema],
    ORG_ISSUES: [orgIssuesSchema],
    users: [usersSchema],
    USER: [userSchema],
    CURRENT_USER: [currentUserSchema],
    GRANTS_LIST: [grantsSchema],
    issues: [issuesSchema],
    ISSUE: issueSchema,
    ISSUE_COMMENTS: [issueCommentsSchema],
    contracts: [orgContractsSchema],
    CONTRACTS_LIST: [contractsSchema],
    UPLOAD_CONTRACTS: [uploadContractsSchema],
    MANAGE_CONTRACTS: [manageContractsSchema],
    packages: [packagesSchema],
    PACKAGE: packageSchema,
    SEARCH_PACKAGES: [searchPackagesSchema],
    AUDIT_DETAILS: auditDetailsSchema
};
